import { type ReactNode, useId } from 'react'

import { Button } from './Button'
import { Label } from './Label'
import { Radio } from './Radio'
import { Wrapper } from './Wrapper'

type Props<TValue> = {
	label: ReactNode
	options: Array<{
		label: ReactNode
		value: TValue
	}>
	selectedValue: TValue

	/** Helps specify the type of the option to help screen-readers read out the localized label in the right language if present. */
	typeOfOptions?: 'locale' | 'default'

	onSelect: (value: TValue) => void
}

export const ButtonSelect = <TValue,>({
	label,
	options,
	selectedValue,
	typeOfOptions = 'default',
	onSelect,
}: Props<TValue>) => {
	const id = useId()
	const labelId = useId()

	return (
		<>
			<Label id={id}>{label}</Label>
			<Wrapper role='radiogroup' aria-labelledby={id}>
				{options.map(({ label, value }, index) => {
					const uniqueLabelId = `${labelId}-${index}`

					return (
						<Button
							key={index}
							aria-checked={selectedValue === value}
							role='radio'
							aria-labelledby={uniqueLabelId}
							onClick={() => onSelect(value)}
						>
							<Radio isChecked={selectedValue === value} />
							<span
								lang={typeOfOptions === 'locale' && typeof value === 'string' ? value : undefined}
								id={uniqueLabelId}
							>
								{label}
							</span>
						</Button>
					)
				})}
			</Wrapper>
		</>
	)
}
